import { useState, useEffect, useRef } from 'react';
import { CATEGORIES } from '../../constants/expenses';

const inputClass =
  'w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 outline-none focus:border-accent focus:ring-2 focus:ring-emerald-100 transition-colors';

const AddExpenseSheet = ({
  open,
  expense,
  initialMonth,
  onSave,
  onUpdate,
  onDelete,
  onClose,
}) => {
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState('');
  const [recurring, setRecurring] = useState(false);
  const [recurringDays, setRecurringDays] = useState([]);
  const [category, setCategory] = useState(undefined);
  const titleRef = useRef(null);

  const isEditing = !!expense;

  useEffect(() => {
    if (!open) return;
    if (expense) {
      setTitle(expense.title);
      setAmount(String(expense.amount));
      setDate(expense.date);
      setRecurring(expense.recurring);
      setRecurringDays(
        expense.recurring
          ? (expense.recurringDays?.length ? expense.recurringDays : [new Date(expense.date + 'T00:00:00').getDate()])
          : []
      );
      setCategory(expense.category);
    } else {
      setTitle('');
      setAmount('');
      const d = `${initialMonth.year}-${String(initialMonth.month).padStart(2, '0')}-01`;
      setDate(d);
      setRecurring(false);
      setRecurringDays([]);
      setCategory(undefined);
      setTimeout(() => titleRef.current?.focus(), 50);
    }
  }, [open, expense, initialMonth]);

  const toggleRecurring = (checked) => {
    setRecurring(checked);
    // Default to the day already picked in the Date field, so the common
    // case (recurs once a month) needs no extra taps.
    if (checked && recurringDays.length === 0) {
      setRecurringDays([new Date(date + 'T00:00:00').getDate()]);
    }
  };

  const toggleDay = (day) => {
    setRecurringDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day].sort((a, b) => a - b)
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      alert('Please enter a title.');
      return;
    }
    const parsed = parseFloat(amount.replace(',', '.'));
    if (isNaN(parsed) || parsed === 0) {
      alert('Please enter a valid amount.');
      return;
    }
    if (recurring && recurringDays.length === 0) {
      alert('Select at least one day of the month this expense recurs on.');
      return;
    }
    const data = {
      title: trimmedTitle,
      amount: parsed,
      date,
      recurring,
      category,
      recurringDays: recurring ? recurringDays : undefined,
    };
    if (isEditing) {
      onUpdate({ ...expense, ...data });
    } else {
      onSave(data);
    }
    onClose();
  };

  const handleDelete = () => {
    if (!expense) return;
    if (!window.confirm(`Delete "${expense.title}"? This can't be undone.`)) return;
    onDelete(expense.id);
    onClose();
  };

  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-gray-900/40 z-[200] transition-opacity ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      />
      <div
        className={`fixed bottom-0 inset-x-0 max-w-lg mx-auto bg-white rounded-t-2xl shadow-xl z-[300] max-h-[92dvh] overflow-y-auto transition-transform duration-300 ease-out ${
          open ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="w-9 h-1 rounded-full bg-gray-200 mx-auto sticky top-3 mt-3 mb-2" />
        <form onSubmit={handleSubmit} className="px-5 pt-2 pb-8 space-y-5">
          <div>
            <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
              {isEditing ? 'Edit title' : 'Title'}
            </label>
            <input
              ref={titleRef}
              className={inputClass}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Rent"
              autoComplete="off"
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
              Category
            </label>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setCategory((c) => (c === cat ? undefined : cat))}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    category === cat
                      ? 'bg-accent text-white border-accent'
                      : 'border-gray-200 text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                Amount
              </label>
              <input
                className={inputClass}
                type="number"
                inputMode="decimal"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                Date
              </label>
              <input
                className={inputClass}
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
          </div>

          <label className="flex items-center gap-3 cursor-pointer w-fit">
            <span className="relative inline-flex items-center flex-shrink-0">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={recurring}
                onChange={(e) => toggleRecurring(e.target.checked)}
              />
              <span className="w-10 h-6 bg-gray-200 peer-checked:bg-accent rounded-full transition-colors" />
              <span className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-4" />
            </span>
            <span className="text-sm font-medium text-gray-700">Recurring</span>
          </label>

          {recurring && (
            <div>
              <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                Repeats on
              </label>
              <div className="grid grid-cols-7 gap-1.5">
                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDay(day)}
                    className={`h-8 rounded-lg text-xs font-medium border transition-colors ${
                      recurringDays.includes(day)
                        ? 'bg-accent text-white border-accent'
                        : 'border-gray-200 text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    {day}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-1.5">
                {recurringDays.length > 1
                  ? `Occurs ${recurringDays.length}× a month, on the ${recurringDays.join(', ')}.`
                  : "Days beyond a month's length move to its last day."}
              </p>
            </div>
          )}

          {isEditing && (
            <button
              type="button"
              onClick={handleDelete}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-negative text-sm font-medium hover:bg-rose-50 transition-colors"
            >
              Delete
            </button>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-500 text-sm hover:text-gray-900 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2.5 bg-accent text-white rounded-xl text-sm font-medium hover:bg-emerald-700 transition-colors shadow-sm"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddExpenseSheet;
